/**
 * IP allow-list matching for the Optigest external API (`/api/v2/*`).
 *
 * Lives in `shared` rather than beside the handler because both ends need the
 * same notion of "is this a valid entry": the admin UI / tRPC input validates
 * what an operator types, and the API matches an inbound request against it.
 * A validator that drifted from the matcher would accept entries that can
 * never match — an allow-list that silently locks the caller out.
 *
 * Supports single IPv4/IPv6 addresses and CIDR ranges (`198.51.100.0/24`,
 * `2001:db8::/32`). No dependency — CIDR is byte-masked over the parsed
 * address, which is why both families are handled as byte arrays.
 */

const IPV4_BYTES = 4;
const IPV6_BYTES = 16;

/** `::ffff:203.0.113.7` → `203.0.113.7`, `::1` → `127.0.0.1`. */
export const normaliseIpText = (raw: string): string => {
  let out = raw.trim().toLowerCase();
  // Bracketed form from a Host-style header: `[::1]`.
  if (out.startsWith('[') && out.endsWith(']')) out = out.slice(1, -1);
  if (out.startsWith('::ffff:') && out.includes('.')) out = out.slice(7);
  if (out === '::1') return '127.0.0.1';
  return out;
};

const parseIpv4 = (text: string): Uint8Array | null => {
  const parts = text.split('.');
  if (parts.length !== IPV4_BYTES) return null;
  const bytes = new Uint8Array(IPV4_BYTES);
  for (let i = 0; i < IPV4_BYTES; i += 1) {
    const part = parts[i] ?? '';
    // Reject empties, non-digits and zero-padded forms (`01`), which some
    // resolvers read as octal — an allow-list must not be ambiguous.
    if (!/^\d{1,3}$/.test(part)) return null;
    if (part.length > 1 && part.startsWith('0')) return null;
    const n = Number(part);
    if (n > 255) return null;
    bytes[i] = n;
  }
  return bytes;
};

const parseIpv6 = (text: string): Uint8Array | null => {
  if (!text.includes(':')) return null;
  const doubleColon = text.indexOf('::');
  if (doubleColon !== text.lastIndexOf('::')) return null;

  // A trailing IPv4 literal (`::ffff:192.0.2.1`) contributes two groups.
  let head = text;
  let tailBytes: Uint8Array | null = null;
  const lastColon = text.lastIndexOf(':');
  const maybeV4 = text.slice(lastColon + 1);
  if (maybeV4.includes('.')) {
    tailBytes = parseIpv4(maybeV4);
    if (!tailBytes) return null;
    head = text.slice(0, lastColon);
  }

  const [left, right] = doubleColon === -1 ? [head, null] : splitOnce(head);
  const leftGroups = left === '' ? [] : left.split(':');
  const rightGroups = right === null || right === '' ? [] : right.split(':');

  const groupBytes = (groups: string[]): Uint8Array | null => {
    const out = new Uint8Array(groups.length * 2);
    for (let i = 0; i < groups.length; i += 1) {
      const g = groups[i] ?? '';
      if (!/^[0-9a-f]{1,4}$/.test(g)) return null;
      const n = Number.parseInt(g, 16);
      out[i * 2] = n >> 8;
      out[i * 2 + 1] = n & 0xff;
    }
    return out;
  };

  const leftBytes = groupBytes(leftGroups);
  const rightBytes = groupBytes(rightGroups);
  if (!leftBytes || !rightBytes) return null;

  const tailLen = tailBytes ? tailBytes.length : 0;
  const used = leftBytes.length + rightBytes.length + tailLen;
  if (used > IPV6_BYTES) return null;
  // Without `::` the address must be exactly full length.
  if (doubleColon === -1 && used !== IPV6_BYTES) return null;
  if (doubleColon !== -1 && used === IPV6_BYTES) return null;

  const bytes = new Uint8Array(IPV6_BYTES);
  bytes.set(leftBytes, 0);
  const tailStart = IPV6_BYTES - tailLen - rightBytes.length;
  bytes.set(rightBytes, tailStart);
  if (tailBytes) bytes.set(tailBytes, IPV6_BYTES - tailLen);
  return bytes;
};

const splitOnce = (text: string): [string, string] => {
  const idx = text.indexOf('::');
  return [text.slice(0, idx), text.slice(idx + 2)];
};

/** Parsed address as bytes, or `null` when `text` is not an IP literal. */
export const parseIp = (text: string): Uint8Array | null => {
  const norm = normaliseIpText(text);
  if (norm.includes(':')) return parseIpv6(norm);
  return parseIpv4(norm);
};

export type AllowlistEntry = {
  bytes: Uint8Array;
  /** Significant leading bits to compare. Full width for a bare address. */
  prefixBits: number;
};

/**
 * Parse one allow-list entry (`203.0.113.7` or `198.51.100.0/24`).
 * Returns `null` for anything unparseable, so callers can reject at the
 * boundary rather than storing an entry that could never match.
 */
export const parseAllowlistEntry = (raw: string): AllowlistEntry | null => {
  const text = raw.trim();
  if (text.length === 0) return null;

  const slash = text.indexOf('/');
  if (slash === -1) {
    const bytes = parseIp(text);
    return bytes ? { bytes, prefixBits: bytes.length * 8 } : null;
  }

  const bytes = parseIp(text.slice(0, slash));
  if (!bytes) return null;
  const maskText = text.slice(slash + 1);
  if (!/^\d{1,3}$/.test(maskText)) return null;
  const prefixBits = Number(maskText);
  if (prefixBits > bytes.length * 8) return null;
  return { bytes, prefixBits };
};

/** True when `raw` is a usable allow-list entry. Used by the tRPC input. */
export const isValidAllowlistEntry = (raw: string): boolean =>
  parseAllowlistEntry(raw) !== null;

const withinPrefix = (
  a: Uint8Array,
  b: Uint8Array,
  prefixBits: number
): boolean => {
  if (a.length !== b.length) return false;
  const fullBytes = prefixBits >> 3;
  for (let i = 0; i < fullBytes; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  const remainingBits = prefixBits & 7;
  if (remainingBits === 0) return true;
  const mask = (0xff << (8 - remainingBits)) & 0xff;
  return ((a[fullBytes] ?? 0) & mask) === ((b[fullBytes] ?? 0) & mask);
};

/**
 * Is `clientIp` permitted by `entries`?
 *
 * **An empty (or all-invalid) list allows everything** — the allow-list is
 * opt-in, so introducing the feature must never lock out an integration that
 * has not configured one. Callers that need "deny by default" must check for
 * emptiness themselves.
 *
 * A `null`/unparseable client IP is denied whenever a non-empty list is
 * configured: if we cannot tell where a request came from, we cannot say it
 * came from an allowed place.
 */
export const isIpAllowed = (
  clientIp: string | null,
  entries: readonly string[] | null | undefined
): boolean => {
  const parsed = (entries ?? [])
    .map(parseAllowlistEntry)
    .filter((e): e is AllowlistEntry => e !== null);
  if (parsed.length === 0) return true;

  const ip = clientIp === null ? null : parseIp(clientIp);
  if (!ip) return false;

  return parsed.some((entry) =>
    withinPrefix(ip, entry.bytes, entry.prefixBits)
  );
};
